import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Badge } from 'react-bootstrap-5';

import ContactTooltip from 'bundles/backoffice/contacts/contact_tooltip';
import Receivables from 'bundles/apex/deals/values/uncollected_amount';
import VoucherApprovalStatusOverlayTrigger from 'bundles/backoffice/vouchers/approval_status/overlay_trigger';
import VoucherTooltip from 'bundles/backoffice/vouchers/index/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { USD } from 'helpers/formatters/number_formats';
import { clickAsLink } from 'helpers/link_helpers';

const rolePriorities = [
  'seller',
  'seller_contact',
  'seller_agent',
  'buyer',
  'buyer_contact',
  'buyer_agent',
  'owner_landlord',
  'owner_user',
  'owner_representative',
  'owner_landlord_agent',
  'tenant',
  'tenant_contact',
  'tenant_agent',
  'tenant_user'
];

export default function VoucherRow({ minimal, voucher }) {
  function getRolePriority(role) {
    const priority = rolePriorities.indexOf(role);
    return priority === -1 ? null : priority;
  }

  const contactRoles = voucher.contactRoles.sort((a, b) => {
    const aPriority = getRolePriority(a.role);
    const bPriority = getRolePriority(b.role);

    if (aPriority != null && bPriority != null) {
      return aPriority < bPriority ? -1 : 1;
    } else if (aPriority != null || b.role === 'payer') {
      return -1;
    } else if (bPriority != null || a.role === 'payer') {
      return 1;
    } else {
      return a.roleLabel < b.roleLabel ? -1 : 1;
    }
  });

  const { outsideCommissionContacts } = voucher;

  if (outsideCommissionContacts && outsideCommissionContacts.length > 0) {
    const outsideCommissionRoles = outsideCommissionContacts.map(contact => ({
      id: `commission-contact-${contact.id}`,
      contactName: contact.name || contact.email,
      roleLabel: 'Outside Commission'
    }));

    contactRoles.concat(outsideCommissionRoles);
  }

  const goToVoucher = (event) => {
    clickAsLink(window, voucher.path, event);
  };

  
  let approvalStatus;
  if (voucher.pendingApproval && voucher.approvalWorkflow) {
    approvalStatus = (
      <VoucherApprovalStatusOverlayTrigger
        approvalWorkflow={voucher.approvalWorkflow}
        completedApprovalTasks={voucher.completedApprovalTasks}
        uncompletedApprovalTasks={voucher.uncompletedApprovalTasks}
      />
    );
  }

  return (
    <tr className="clickable" onClick={goToVoucher}>
      <td className="voucher-column">
        <VoucherTooltip voucher={voucher} />
      </td>
      <td>{voucher.closeDate ? formatDate(voucher.closeDate) : '--'}</td>
      <td>{voucher.identifier}</td>
      <td>
        <Badge bg={voucher.statusPillClass}>
          {voucher.statusLabel}
          {approvalStatus}
        </Badge>
      </td>
      {!minimal && <td className="text-nowrap">{voucher.dealTypeLabel}</td>}
      {!minimal && <td className="text-nowrap">{voucher.propertyTypeLabel || '--'}</td>}
      <td className="text-nowrap">
        {contactRoles.length === 0 ? '--' : (
          <ContactTooltip contactRoles={contactRoles}/>
        )}
      </td>
      <td className="text-end">
        {voucher.transactionValue == null ? '--' : USD.format(voucher.transactionValue)}
      </td>
      <td className="text-end">
        {voucher.grossCommissionAmount == null ? '--' : USD.format(voucher.grossCommissionAmount)}
      </td>
      {!minimal &&
        <td className="text-end text-nowrap">
          <Receivables deal={{ ...voucher, voucher_association: {} }} />
        </td>}
    </tr>
  );
}

VoucherRow.propTypes = {
  minimal: PropTypes.bool,
  voucher: PropTypes.shape({
    approvalWorkflow: PropTypes.object,
    closeDate: PropTypes.string,
    completedApprovalTasks: PropTypes.array,
    contactRoles: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string,
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      role: PropTypes.string.isRequired,
      roleLabel: PropTypes.string.isRequired
    })).isRequired,
    dealTypeLabel: PropTypes.string,
    grossCommissionAmount: PropTypes.number,
    identifier: PropTypes.string.isRequired,
    oneLineLabel: PropTypes.string.isRequired,
    outsideCommissionContacts: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string,
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
    })),
    path: PropTypes.string.isRequired,
    pendingApproval: PropTypes.bool,
    propertyTypeLabel: PropTypes.string,
    statusLabel: PropTypes.string.isRequired,
    statusPillClass: PropTypes.string.isRequired,
    transactionValue: PropTypes.number,
    uncompletedApprovalTasks: PropTypes.array,
  })
};
